import type { OccupancyCell } from "./OccupancyLayer";
import type { ProductCatalogRow } from "../api/rest";

/**
 * Product search over a run's occupancy cells (ADR 196): the /live "find my
 * product" box. Pure (no three.js/React) so the matching vocabulary — raw
 * scanned label, alias-translated WMS item, WMS catalog description — is
 * unit-tested on its own.
 *
 * A cell matches when the normalized query is a substring of ANY of:
 *  - `product` — the raw scanned pallet/box label ("item id" as typed on the floor),
 *  - `item`    — the same label under the alias translation (the WMS item code),
 *  - the catalog description of that item (only present after a WMS import —
 *    with no snapshot the search silently degrades to the two codes).
 *
 * ADR 203 adds the WMS-attribute predicates (dwell / expiry / mfg), AND-combined
 * with the text. Different populations by design: the TEXT predicate speaks the
 * floor (counted cells' scan labels), the DATE predicates speak the WMS snapshot
 * for the ADDRESS — a date-only search may light a bin the run found empty or
 * unidentified, which is exactly where you send someone.
 */

/** Case-, accent- and spacing-insensitive: "Cushão  main" ≡ "CUSHAO MAIN". */
export function normalizeSearchText(s: string): string {
  return s
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toUpperCase()
    .replace(/\s+/g, " ")
    .trim();
}

/** A query starts filtering at 2 normalized characters — a single letter would
 *  ghost the whole warehouse on the first keystroke. */
export function isActiveQuery(query: string): boolean {
  return normalizeSearchText(query).length >= 2;
}

/** ADR 203: the finder's WMS-attribute predicates, as typed ("" = off).
 *  Dates are ISO yyyy-mm-dd (the native <input type="date"> value). */
export interface ProductFilters {
  dwellOp: "gte" | "lte";
  dwellDays: string;
  expiryOp: "lte" | "gte";
  expiryDate: string;
  mfgOp: "lte" | "gte";
  mfgDate: string;
}

export const EMPTY_FILTERS: ProductFilters = {
  dwellOp: "gte", dwellDays: "",
  expiryOp: "lte", expiryDate: "",
  mfgOp: "lte", mfgDate: "",
};

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/** Criteria that will actually filter — a half-typed value is inactive, never an error. */
export function activeFilterCount(f: ProductFilters): number {
  let n = 0;
  if (/^\d+$/.test(f.dwellDays.trim())) n++;
  if (ISO_DATE.test(f.expiryDate)) n++;
  if (ISO_DATE.test(f.mfgDate)) n++;
  return n;
}

export function isActiveSearch(query: string, filters: ProductFilters): boolean {
  return isActiveQuery(query) || activeFilterCount(filters) > 0;
}

/** Whole days from `fromIso` to `todayIso` (both yyyy-mm-dd), UTC-anchored so a
 *  timezone can never shift a dwell across a day boundary. */
export function daysBetween(fromIso: string, todayIso: string): number {
  return Math.floor((Date.parse(todayIso) - Date.parse(fromIso)) / 86_400_000);
}

function passDate(cellIso: string | null | undefined, op: "lte" | "gte", filterIso: string): boolean {
  if (!cellIso) return false; // no WMS data at the address = the predicate cannot vouch for it
  return op === "lte" ? cellIso <= filterIso : cellIso >= filterIso;
}

/**
 * Normalized item code → normalized description, the description-search index.
 * Exported so a caller holding a stable catalog can build it ONCE (ADR 212):
 * JSL's catalog is ~52k rows, and rebuilding this map on every keystroke was
 * pure waste inside the search hot path.
 */
export type CatalogIndex = Map<string, string>;

export function catalogDescriptions(catalog: ProductCatalogRow[]): CatalogIndex {
  const out: CatalogIndex = new Map();
  for (const row of catalog) {
    if (row.description) out.set(normalizeSearchText(row.product), normalizeSearchText(row.description));
  }
  return out;
}

/**
 * The matched addresses, or null when nothing is active — the layers use null
 * to mean "no search: render normally", distinct from an empty set, which means
 * "search found nothing: ghost everything". Active predicates AND together.
 *
 * `todayIso` anchors the dwell computation (yyyy-mm-dd) — passed in so the
 * matcher stays pure and the tests own the clock.
 *
 * `catalog` takes either the raw rows (indexed on the spot — the shape the
 * tests use) or a prebuilt `CatalogIndex` when the caller already memoized it.
 */
export function matchProducts(
  cells: OccupancyCell[], catalog: ProductCatalogRow[] | CatalogIndex, query: string,
  filters: ProductFilters = EMPTY_FILTERS, todayIso = "1970-01-01",
): Set<string> | null {
  const nq = normalizeSearchText(query);
  const textActive = nq.length >= 2;
  const dwellActive = /^\d+$/.test(filters.dwellDays.trim());
  const expiryActive = ISO_DATE.test(filters.expiryDate);
  const mfgActive = ISO_DATE.test(filters.mfgDate);
  if (!textActive && !dwellActive && !expiryActive && !mfgActive) return null;

  const descByProduct = catalog instanceof Map ? catalog : catalogDescriptions(catalog);
  const dwellWant = dwellActive ? parseInt(filters.dwellDays.trim(), 10) : 0;

  const addresses = new Set<string>();
  for (const cell of cells) {
    if (textActive) {
      if (cell.status !== "counted" || !cell.product) continue;
      const raw = normalizeSearchText(cell.product);
      const item = cell.item ? normalizeSearchText(cell.item) : raw;
      const desc = descByProduct.get(item) ?? descByProduct.get(raw) ?? "";
      if (!raw.includes(nq) && !item.includes(nq) && !desc.includes(nq)) continue;
    }
    if (dwellActive) {
      if (!cell.fifoDate) continue;
      const dwell = daysBetween(cell.fifoDate, todayIso);
      if (filters.dwellOp === "gte" ? dwell < dwellWant : dwell > dwellWant) continue;
    }
    if (expiryActive && !passDate(cell.expiryDate, filters.expiryOp, filters.expiryDate)) continue;
    if (mfgActive && !passDate(cell.mfgDate, filters.mfgOp, filters.mfgDate)) continue;
    addresses.add(cell.address);
  }
  return addresses;
}
